import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, X, ChevronRight } from "lucide-react";
import { useSim } from "../lib/store";

const STOPS = [
  { year: "1960", name: "Valdivia, Chile", mag: 9.5, dur: 90, lat: -38.14, lon: -73.41,
    caption: "The most powerful earthquake ever recorded. A 1,000 km fault rupture unleashed a Pacific-wide tsunami that struck Hawaii and Japan a day later." },
  { year: "1964", name: "Great Alaska", mag: 9.2, dur: 80, lat: 60.91, lon: -147.34,
    caption: "Four and a half minutes of shaking liquefied the ground beneath Anchorage. Land dropped and rose by metres across a vast area of the subduction zone." },
  { year: "2004", name: "Sumatra–Andaman", mag: 9.1, dur: 85, lat: 3.3, lon: 95.98,
    caption: "A rupture over 1,300 km long lifted the sea floor and generated the deadliest tsunami in modern history across the Indian Ocean." },
  { year: "2011", name: "Tōhoku, Japan", mag: 9.1, dur: 75, lat: 38.30, lon: 142.37,
    caption: "The offshore megathrust drove a tsunami up to 40 m high, overwhelming sea walls and reshaping our understanding of subduction hazard." },
  { year: "1906", name: "San Francisco", mag: 7.9, dur: 55, lat: 37.75, lon: -122.55,
    caption: "The San Andreas Fault slipped up to 6 m. Fire, not shaking, caused most of the destruction — a defining moment for earthquake science." },
];

const DURATION = 9500;

export function GuidedTour() {
  const tourActive = useSim((s) => s.tourActive);
  const viewMode = useSim((s) => s.viewMode);
  const simulating = useSim((s) => s.simulating);
  const startTour = useSim((s) => s.startTour);
  const stopTour = useSim((s) => s.stopTour);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (tourActive) setIdx(0);
  }, [tourActive]);

  useEffect(() => {
    if (!tourActive) return;
    const s = STOPS[idx];
    const st = useSim.getState();
    st.resetEpicenterFlow();
    st.setTourFocus({ lat: s.lat, lon: s.lon });
    st.addEvent({ lat: s.lat, lon: s.lon, magnitude: s.mag, duration: s.dur, type: "seismic" });
    st.executeAll();
    const t = setTimeout(() => {
      if (idx + 1 < STOPS.length) setIdx(idx + 1);
      else useSim.getState().stopTour();
    }, DURATION);
    return () => clearTimeout(t);
  }, [tourActive, idx]);

  const next = () => {
    if (idx + 1 < STOPS.length) setIdx(idx + 1);
    else stopTour();
  };

  return (
    <>
      {/* trigger — discoverable for first-time visitors */}
      <AnimatePresence>
        {!tourActive && viewMode === "macro" && (
          <motion.button
            data-testid="guided-tour-start"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            onClick={startTour}
            className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-[rgba(201,169,74,0.5)] text-[#e9dcb8] hover:text-white hover:border-[rgba(201,169,74,0.9)] shadow-[0_8px_30px_rgba(0,0,0,0.7)] transition-colors"
          >
            <Compass className="h-4 w-4 text-[#c9a94a]" />
            <span className="text-xs font-semibold tracking-wide">Guided Tour</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* caption card */}
      <AnimatePresence>
        {tourActive && (
          <motion.div
            key={idx}
            data-testid="tour-caption"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 w-[92vw] max-w-xl p-5 rounded-2xl bg-zinc-950/85 backdrop-blur-2xl border border-[rgba(201,169,74,0.35)] shadow-[0_20px_60px_rgba(0,0,0,0.9)]"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-baseline gap-3">
                  <span style={{ fontFamily: '"Playfair Display", Georgia, serif' }} className="text-3xl font-semibold text-[#c9a94a]">
                    {STOPS[idx].year}
                  </span>
                  <span style={{ fontFamily: 'Georgia, serif' }} className="text-lg text-[#f4ede0]">
                    {STOPS[idx].name}
                  </span>
                  <span className="font-mono-tele text-sm font-bold text-red-500">Mw {STOPS[idx].mag.toFixed(1)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">{STOPS[idx].caption}</p>
              </div>
              <button
                data-testid="tour-exit"
                onClick={stopTour}
                className="shrink-0 p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                title="Exit tour"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {STOPS.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#c9a94a]" : "w-1.5 bg-white/20"}`} />
                ))}
              </div>
              <button
                data-testid="tour-next"
                onClick={next}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-red-600/85 hover:bg-red-500 text-white text-xs font-semibold transition-colors"
              >
                {idx + 1 < STOPS.length ? "Next" : "Finish"} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
